import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/ui/data-table";
import { UserAvatar } from "@/components/common/UserAvatar";
import type { DashboardAffiliate, StatValue } from "@/types/dashboard";
import { formatAmount, formatNumber } from "@/utils/formatters";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { Flag } from "./affiliate-map";
import { countryName } from "./country-geometry";

const columns: ColumnDef<DashboardAffiliate>[] = [
  {
    accessorKey: "name",
    header: "Affiliate",
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
          <UserAvatar
            src={row.original.profileImage ?? undefined}
            name={row.original.name}
            className="h-full w-full text-xs"
          />
        </span>
        <span className="block max-w-[10rem] truncate font-medium text-foreground">{row.original.name}</span>
      </span>
    ),
  },
  {
    accessorKey: "country",
    header: "Country",
    meta: { fit: true },
    cell: ({ row }) =>
      row.original.country ? (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <Flag code={row.original.country} />
          <span className="block max-w-[6rem] truncate">{countryName(row.original.country)}</span>
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: "orders",
    header: "Orders",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.orders)}</span>,
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    meta: { fit: true },
    cell: ({ row }) => (
      <span className="tabular-nums font-medium text-foreground">{formatAmount(row.original.revenue)}</span>
    ),
  },
  {
    accessorKey: "commission",
    header: "Commission",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatAmount(row.original.commission)}</span>,
  },
];

export function TopAffiliatesCard({
  data,
  stats,
  isLoading,
}: {
  data: DashboardAffiliate[] | undefined;
  stats?: StatValue[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  return (
    <DashboardCard
      id="dashboard-top-affiliates-card"
      title="Top affiliates"
      to="/brand/affiliates/active"
      stats={stats}
      bodyClassName="px-3 pb-3"
    >
      {/* The API sends at most five; the body keeps five rows' height so
          the card never changes size between periods. */}
      <div className="min-h-[14.5rem]">
        <DataTable
          columns={columns}
          data={(data ?? []).slice(0, 5)}
          isLoading={isLoading}
          skeletonRows={5}
          getRowId={(row) => row.affiliateId}
          onRowClick={(row) => navigate(`/brand/affiliates/${row.affiliateId}`)}
          emptyText="No sales in this period"
          minTableWidth="min-w-0"
        />
      </div>
    </DashboardCard>
  );
}
